import type { Academy } from '@/dto'

import type { PayStatusAuthCheckerFn, RoutePermissionType } from './RoutePermission.type'

export class RoutePermission {
  private _routePermissionByPayStatus: RoutePermissionType

  get permissionByStatus() {
    return this._routePermissionByPayStatus
  }

  constructor(
    userAuthority: res.Teacher['type'],
    payStatus: Academy['payStatus'],
    isLimited: boolean,
    isSeoltab: boolean,
    isFranchise: boolean,
  ) {
    if (this._checkPermission(userAuthority, 'ROOT_TEACHER')) {
      this._routePermissionByPayStatus = this._ROOT_TEACHER(
        payStatus,
        isLimited,
        isSeoltab,
        isFranchise,
      )
    } else if (this._checkPermission(userAuthority, 'HOMEROOM_TEACHER')) {
      this._routePermissionByPayStatus = this._HOMEROOM_TEACHER(
        payStatus,
        isLimited,
        isSeoltab,
        isFranchise,
      )
    } else {
      this._routePermissionByPayStatus = this._MANAGER_TEACHER(
        payStatus,
        isLimited,
        isSeoltab,
        isFranchise,
      )
    }
  }

  // 관리자 선생님
  private _ROOT_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isLimited,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사용가이드: { access: !isLimited },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: true,
          정보수정: true,
          삭제: true,
        },
        class: {
          access: true,
          myClassOnly: false,
          등록: true,
          삭제: true,
        },
        teacher: {
          access: true,
          등록: true,
          정보수정: true,
        },
        attendance: {
          access: true,
        },
        tuition: {
          access: true,
        },
      },
      고객지원: {
        access: !isLimited,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        학습지만들기: {
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        다른기관학습지: {
          access: true,
        },
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }
  // 전체 권한 선생님
  private _MANAGER_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isLimited,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사용가이드: { access: !isLimited },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: true,
          정보수정: true,
          삭제: true,
        },
        class: {
          access: true,
          myClassOnly: false,
          등록: true,
          삭제: true,
        },
        teacher: {
          access: false,
          등록: false,
          정보수정: false,
        },
        attendance: {
          access: true,
        },
        tuition: {
          access: true,
        },
      },
      고객지원: {
        access: !isLimited,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        학습지만들기: {
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        다른기관학습지: {
          access: true,
        },
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }
  // 담당 권한 선생님
  private _HOMEROOM_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isLimited,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사용가이드: { access: !isLimited },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: false,
          정보수정: true,
          삭제: false,
        },
        class: {
          access: true,
          myClassOnly: true,
          등록: false,
          삭제: false,
        },
        teacher: {
          access: false,
          등록: false,
          정보수정: false,
        },
        attendance: {
          access: true,
        },
        tuition: {
          access: true,
        },
      },

      고객지원: {
        access: !isLimited,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        학습지만들기: {
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        다른기관학습지: {
          access: true,
        },
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }

  private _checkPermission(
    userAuthority: res.Teacher['type'],
    target?: res.Teacher['type'],
  ): boolean {
    if (!target) return false
    return userAuthority === target
  }
}

const defaultRoutePermission: RoutePermissionType = {
  myinfo: {
    access: false,
    기관정보: { access: false },
    사용가이드: { access: false },
    교재구매내역: { access: false },
  },
  // 운영 관리
  manage: {
    access: false,
    student: {
      등록: false,
      정보수정: false,
      삭제: false,
    },
    // 반 관리
    class: {
      access: false,
      myClassOnly: false,
      등록: false,
      삭제: false,
    },
    // 선생님 관리
    teacher: {
      access: false,
      등록: false,
      정보수정: false,
    },
    // 출결 관리
    attendance: {
      access: false,
    },
    tuition: {
      access: false,
    },
  },
  고객지원: {
    access: false,
  },
  // 수업 준비
  prepare: {
    access: false,
    학습지만들기: {
    },
    학습지: {
      access: false,
      내학습지: {
        access: false,
      },
      즐겨찾는문제: {
        access: false,
      },
    },
    내교재: {
      access: false,
    },
    다른기관학습지: {
      access: false,
    },
  },
  lesson: {
    access: false,
  },
  orderManage: {
    access: false,
  },
  notification: {
    access: false,
  },
}

const seoltabRoutePermission: RoutePermissionType = {
  ...defaultRoutePermission,
  prepare: {
    ...defaultRoutePermission.prepare,
    access: true,
    학습지만들기: {
    },
    학습지: {
      access: true,
      내학습지: {
        access: true,
      },
      즐겨찾는문제: {
        access: true,
      },
    },
    내교재: {
      access: false,
    },
    다른기관학습지: {
      access: false,
    },
  },
}
